import { useState } from 'react';
import { useSelector } from 'react-redux';
import { CancelClarificationButton } from '@frontend/components/elements/carePathButtons/CancelClarificationButton';
import type { IReduxState } from '../../../configureStore';
import type { IDiagnosticReportGetOne } from '../../interfaces/diagnosticReportGetOne';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import type { IReportItemState } from '../episodes/Episode';
import type { IEpisodeContext } from '../episodes/EpisodeContext';
import { ReportStatus } from '../../../../common/enums/reportStatus';
import { ToastType } from '../../../../common/enums/toastType';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import {
    diagnosticReportUpdateClarificationPerformerSchema,
    diagnosticReportUpdateClarificationRequesterSchema,
} from '../../../../common/validation/diagnosticReport';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { useEpisodeContext } from '../../../utils/hooks/useEpisodeContext';
import { showToast } from '../../../utils/showToast';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { BecomeRequesterButton } from '../../elements/carePathButtons/BecomeRequesterButton';
import { ConfirmDiagnosticReportButton } from '../../elements/carePathButtons/ConfirmDiagnosticReportButton';
import { ConvertToReferralButton } from '../../elements/carePathButtons/ConvertToReferralButton';
import { PrintCarePathButton } from '../../elements/carePathButtons/PrintCarePathButton';
import { SendClarificationButton } from '../../elements/carePathButtons/SendClarificationButton';
import { TakeDiagnosticReportButton } from '../../elements/carePathButtons/TakeDiagnosticReportButton';
import { ButtonContainer, Form, InputTextArea, SuccessFlashWhenSaved } from '../../elements/common';
import { FormLabel } from '../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../elements/forms/InputContainer';
import { CustomControlCollapseBody } from '../customControls/utils/v1/CustomControlCollapseBody';
import { ClarificationMessage } from '../episodes/common';
import { ProcessItemFooter } from '../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../episodes/ProcessItemHeader';

interface IClarificationItemOpenProps {
    acceptDiagnosticReport: () => Promise<void>;
    diagnosticReport: IDiagnosticReportGetOne;
    isLastItem: boolean;
    isPreviousRequest: boolean;
    itemState: IReportItemState;
    onSave: () => Promise<void>;
    reloadProcess: () => Promise<void>;
    request: IRequestGetOne;
    updateItemState: (itemState: IReportItemState) => Promise<void>;
}

interface IDiagnosticReport {
    clarificationQAPerformer: string | null;
    clarificationQARequester: string | null;
    clarificationQARequesterPractitionerId: string | null;
}

export function ClarificationItemOpen(props: IClarificationItemOpenProps) {
    const [diagnosticReport, setDiagnosticReport] = useState<IDiagnosticReport>(setClarificationState());
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState(emptyValidationErrors);
    const [savedCounter, setSavedCounter] = useState(0);
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const { patient, onConvertToReferral }: IEpisodeContext = useEpisodeContext();

    if (!diagnosticReport || !props.diagnosticReport.authorization) {
        return null;
    }

    const isShowMoreVisible = !!props.diagnosticReport.clarificationQARequester;
    const itemIsOpen = props.itemState.addClarification || (isShowMoreVisible && props.itemState.isClarificationOpen);

    return (
        <>
            <ProcessItemHeader
                date={props.diagnosticReport.dateSent}
                isFirstItem={false}
                isItemOpen={itemIsOpen}
                onToggle={async (isOpen) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isClarificationOpen: isOpen,
                        addClarification: false,
                    })
                }
                process={undefined}
                title={translateKey('diagnosticReport.clarification')}
            />

            <CustomControlCollapseBody isOpen={itemIsOpen}>
                <Form onSubmit={async () => null}>
                    <SuccessFlashWhenSaved
                        message={translateKey('diagnosticReport.clarificationSentMessage')}
                        savedCounter={savedCounter}
                    />

                    {/* Requester asking performer a question */}
                    {props.diagnosticReport.clarificationQARequester && (
                        <ClarificationMessage
                            sender={
                                props.diagnosticReport.clarificationQARequesterPractitioner
                                    ? getDisplayName(props.diagnosticReport.clarificationQARequesterPractitioner)
                                    : null
                            }
                            text={props.diagnosticReport.clarificationQARequester}
                        />
                    )}

                    {props.diagnosticReport.authorization.canAddDiagnosticReportClarificationRequester &&
                        !props.diagnosticReport.clarificationQARequester &&
                        props.diagnosticReport.reviewStatus === ReportStatus.open && (
                            <HorizontalFormGroup hasErrors={errors.has('clarificationQARequester')}>
                                <FormLabel isRequired>{translateKey('diagnosticReport.clarification')}</FormLabel>
                                <InputContainer>
                                    <InputTextArea
                                        automation="clarificationText"
                                        error={errors.get('clarificationQARequester')}
                                        onChange={async (value) =>
                                            await handleChange('clarificationQARequester', value)
                                        }
                                        onDoValidate={async () => await validateOne('clarificationQARequester')}
                                        placeholder={translateKey('diagnosticReport.clarification')}
                                        reserveErrorSpace={true}
                                        value={diagnosticReport.clarificationQARequester}
                                    />
                                </InputContainer>
                            </HorizontalFormGroup>
                        )}

                    {!props.diagnosticReport.authorization.canAddDiagnosticReportClarificationRequester &&
                        !props.diagnosticReport.clarificationQARequester && (
                            <>{translateKey('diagnosticReport.noQuestion')}</>
                        )}

                    {/* performer answering question */}
                    {props.diagnosticReport.clarificationQAPerformer && (
                        <ClarificationMessage
                            sender={
                                props.diagnosticReport.clarificationQAPerformerPractitioner
                                    ? getDisplayName(props.diagnosticReport.clarificationQAPerformerPractitioner)
                                    : null
                            }
                            text={props.diagnosticReport.clarificationQAPerformer}
                        />
                    )}

                    {props.diagnosticReport.authorization.canAddDiagnosticReportClarificationPerformer &&
                        !props.diagnosticReport.clarificationQAPerformer &&
                        props.diagnosticReport.reviewStatus === ReportStatus.open && (
                            <HorizontalFormGroup hasErrors={errors.has('clarificationQAPerformer')}>
                                <FormLabel isRequired>
                                    {translateKey('diagnosticReport.clarificationReaction')}
                                </FormLabel>
                                <InputContainer>
                                    <InputTextArea
                                        automation="answerText"
                                        error={errors.get('clarificationQAPerformer')}
                                        onChange={async (value) =>
                                            await handleChange('clarificationQAPerformer', value)
                                        }
                                        onDoValidate={async () => await validateOne('clarificationQAPerformer')}
                                        placeholder={translateKey('diagnosticReport.clarificationReaction')}
                                        reserveErrorSpace={true}
                                        value={diagnosticReport.clarificationQAPerformer}
                                    />
                                </InputContainer>
                            </HorizontalFormGroup>
                        )}
                </Form>
            </CustomControlCollapseBody>

            {isShowMoreVisible && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isClarificationOpen}
                    labelLess={translateKey('diagnosticReport.hideClarification')}
                    labelMore={translateKey('diagnosticReport.showClarification')}
                    onToggle={async (isOpen) =>
                        await props.updateItemState({
                            ...props.itemState,
                            isClarificationOpen: isOpen,
                            addClarification: false,
                        })
                    }
                />
            )}

            {!isManager && (
                <>
                    {props.diagnosticReport.reviewStatus === ReportStatus.open &&
                    ((props.diagnosticReport.authorization.canAddDiagnosticReportClarificationRequester &&
                        !props.diagnosticReport.clarificationQARequester) ||
                        (props.diagnosticReport.authorization.canAddDiagnosticReportClarificationPerformer &&
                            !props.diagnosticReport.clarificationQAPerformer)) ? (
                        <ButtonContainer
                            alignRight
                            buttons={[
                                <CancelClarificationButton
                                    diagnosticReport={props.diagnosticReport}
                                    onCancel={async () =>
                                        await props.updateItemState({
                                            ...props.itemState,
                                            isClarificationOpen: false,
                                            addClarification: false,
                                        })
                                    }
                                />,
                                <SendClarificationButton
                                    diagnosticReport={props.diagnosticReport}
                                    isDisabled={isSaving}
                                    sendClarification={async () => await saveDiagnosticReport()}
                                />,
                            ]}
                            hideButtons={!props.isLastItem}
                        />
                    ) : (
                        <ButtonContainer
                            alignRight
                            buttons={[
                                <PrintCarePathButton
                                    isLastItem={props.isLastItem}
                                    request={props.request}
                                />,
                                <BecomeRequesterButton
                                    diagnosticReport={props.diagnosticReport}
                                    reloadProcess={props.reloadProcess}
                                    request={props.request}
                                />,
                                <TakeDiagnosticReportButton
                                    diagnosticReport={props.diagnosticReport}
                                    reloadProcess={props.reloadProcess}
                                />,
                                <ConvertToReferralButton
                                    acceptDiagnosticReport={props.acceptDiagnosticReport}
                                    diagnosticReport={props.diagnosticReport}
                                    isPreviousRequest={props.isPreviousRequest}
                                    onConvertToReferral={onConvertToReferral}
                                    patient={patient}
                                    request={props.request}
                                    serviceCode={props.request.service.code}
                                    serviceId={props.request.serviceId}
                                    serviceType={props.request.serviceType}
                                />,
                                <ConfirmDiagnosticReportButton
                                    acceptDiagnosticReport={props.acceptDiagnosticReport}
                                    diagnosticReport={props.diagnosticReport}
                                    patient={patient}
                                    request={props.request}
                                    requestMessageToGP={props.diagnosticReport.authorization.canRequestMessageToGP}
                                    serviceCode={props.request.service.code}
                                />,
                            ]}
                            hideButtons={!props.isLastItem}
                        />
                    )}
                </>
            )}
        </>
    );

    async function handleChange<K extends keyof IDiagnosticReport>(
        name: keyof IDiagnosticReport,
        value: IDiagnosticReport[K] | null
    ) {
        const updatedDiagnosticReport = { ...diagnosticReport, [name]: value };
        setDiagnosticReport(updatedDiagnosticReport);
    }

    async function validateOne(name: keyof IDiagnosticReport) {
        if (name === 'clarificationQARequester') {
            setErrors(
                updateValidationErrorsForOne(
                    diagnosticReportUpdateClarificationRequesterSchema,
                    diagnosticReport,
                    errors,
                    name
                )
            );
        } else {
            setErrors(
                updateValidationErrorsForOne(
                    diagnosticReportUpdateClarificationPerformerSchema,
                    diagnosticReport,
                    errors,
                    name
                )
            );
        }
    }

    async function saveDiagnosticReport() {
        setIsSaving(true);

        if (!diagnosticReport) {
            setIsSaving(false);

            return;
        }

        let urlRoute = '';
        let validationErrors;
        let validatedItem;
        if (!diagnosticReport.clarificationQARequesterPractitionerId) {
            const { errors: updatedRequesterErrors, item } = validate(
                diagnosticReportUpdateClarificationRequesterSchema,
                diagnosticReport
            );
            validationErrors = updatedRequesterErrors;
            validatedItem = item;
            urlRoute = 'updateClarificationRequester';
        } else {
            const { errors: UpdatedPerformerErrors, item } = validate(
                diagnosticReportUpdateClarificationPerformerSchema,
                diagnosticReport
            );
            validationErrors = UpdatedPerformerErrors;
            validatedItem = item;
            urlRoute = 'updateClarificationPerformer';
        }

        setErrors(validationErrors);
        if (validationErrors.size > 0) {
            setIsSaving(false);

            return;
        }

        const url = `/api/diagnosticReports/${props.diagnosticReport.id}/${urlRoute}`;
        await fetchJson(url, 'POST', validatedItem);
        await props.onSave();

        await props.reloadProcess();

        if (diagnosticReport.clarificationQAPerformer) {
            showToast(ToastType.success, translateKey('toast.answerSendSuccess'));
        } else {
            showToast(ToastType.success, translateKey('toast.clarificationSendSuccess'));
        }

        setIsSaving(false);
        setSavedCounter(savedCounter + 1);
    }

    function setClarificationState() {
        if (props.diagnosticReport) {
            return {
                clarificationQARequester: props.diagnosticReport.clarificationQARequester || null,
                clarificationQAPerformer: props.diagnosticReport.clarificationQAPerformer || null,
                clarificationQARequesterPractitionerId:
                    props.diagnosticReport.clarificationQARequesterPractitionerId || null,
            };
        } else {
            return {
                clarificationQARequester: null,
                clarificationQAPerformer: null,
                clarificationQARequesterPractitionerId: null,
            };
        }
    }
}
